import { useContext, useEffect, useMemo, useState } from "react";
import { WebsocketContext } from "../../contexts/WebsocketAuthContext";
import { AssetResponse } from "../../utils/asset";
import { forEachFrame, readFrame } from "./parseFrame";
import { IWSSIndexResponse, IWSSIndexResponseData } from "./model";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { secondsToNanos } from "../../utils/date";
import { knownDisplayPrice } from "../../utils/markGuards";
import { knownPrice } from "../../utils/unknownNumbers";
import { MARK_WS_STALE_MS } from "./useMarkPriceWSS";

const indexChannel = "index";
const INDEX_STALE_CHECK_MS = 15_000;

const useIndexWSS = (asset?: AssetResponse) => {
  const { triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);
  const { activePerpMarkets } = useContext(MarketInstrumentContext);

  const [index, setIndex] = useState<IWSSIndexResponseData | undefined>();
  const [receivedAt, setReceivedAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const indexOrFallback: IWSSIndexResponseData | undefined = useMemo(() => {
    if (!index) {
      const market = activePerpMarkets?.find(
        (m) => m.underlying_asset === asset
      );
      // A null (or "0") REST index is unknown, not a $0 index.
      if (knownPrice(market?.index_price) !== undefined) {
        return {
          timestamp: String(secondsToNanos(Date.now() / 1000)),
          price: market?.index_price,
        };
      }
    }
    return index;
  }, [activePerpMarkets, asset, index]);

  // Whenever asset changed, reset stale ws index price
  useEffect(() => {
    setIndex(undefined);
  }, [asset]);

  useEffect(() => {
    if (!asset) return undefined;
    const data = [`${indexChannel}:${asset}`];
    triggerSubscribe("subscribe", data);
    return () => triggerUnsubscribe(data);
  }, [asset, triggerSubscribe, triggerUnsubscribe]);

  // Receives messages and updates state
  useEffect(() => {
    if (lastMessages && asset) {
      forEachFrame(lastMessages, "useIndexWSS", (lastMessage) => {
        const frame = readFrame<IWSSIndexResponse>(lastMessage);
        if (!frame) return;
        const { data, channel } = frame;
        if (channel === `${indexChannel}:${asset}` && data) {
          // The WS feed sends the raw 18-dec on-chain price; the REST fallback
          // above is already display-scaled. Normalize so `index.price` is
          // always display-ready (fixes the index price AND the OI-notional,
          // which multiplies by it). See utils/displayDecimals.
          // A frame that omits the price says it is unknown right now: keep it
          // undefined (never "0"), and do not fall back to the older REST index.
          setIndex({
            ...data,
            price: knownDisplayPrice(data.price) as string,
          });
          const at = Date.now();
          setReceivedAt(at);
          setNow(at);
        }
      });
    }
  }, [asset, lastMessages]);

  // A socket that delivered once and then went quiet must stop counting as
  // live, or the header would show that index forever. Same rule as marks.
  const hasIndex = index !== undefined;
  useEffect(() => {
    if (!hasIndex) return undefined;
    const id = setInterval(() => setNow(Date.now()), INDEX_STALE_CHECK_MS);
    return () => clearInterval(id);
  }, [hasIndex]);

  return {
    index: indexOrFallback,
    /** True while the websocket has delivered a price in the last 30s; false while `index` is the /markets fallback or the stream has gone quiet. */
    isLive: index !== undefined && now - receivedAt < MARK_WS_STALE_MS,
  };
};

export default useIndexWSS;
